import React, { Component } from "react";
import ApexCharts from "apexcharts";
import "./evs-attack-vector-gauge.css";

const GAUGE_SIZE = 170;
const GAUGE_CX = GAUGE_SIZE / 2;
const GAUGE_CY = GAUGE_SIZE / 2;
const GAUGE_R = Math.round((170 * GAUGE_SIZE) / 420);
const GAUGE_START = 150;
const GAUGE_TOTAL = 240;
const GAUGE_SEG = GAUGE_TOTAL / 4;
const GAUGE_GAP = 4;
const GAUGE_STROKE_WIDTH = 9;

const SEGMENT_COLORS = ["#287050", "#eebc44", "#ea2727", "#c3291e"];

const getSeveritySegmentIndex = (value) => {
	const v = Math.max(0, Math.min(100, Number(value) || 0));
	if (v < 25) return 0; // Low
	if (v < 50) return 1; // Medium
	if (v < 75) return 2; // High
	return 3; // Critical
};

function polar(cx, cy, r, deg) {
	const rad = (deg * Math.PI) / 180;
	return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcD(cx, cy, r, a1, a2) {
	const p1 = polar(cx, cy, r, a1);
	const p2 = polar(cx, cy, r, a2);
	return `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y}`;
}

export default class EvsAttackVectorGauge extends Component {
	state = { chart: null };

	chartId = "evs-attack-vector-gauge-chart";
	wrapperRef = React.createRef();
	blobRef = React.createRef();

	buildChartOptions = () => ({
		chart: {
			type: "radialBar",
			height: GAUGE_SIZE,
			sparkline: { enabled: true },
		},
		series: [100],
		plotOptions: {
			radialBar: {
				startAngle: GAUGE_START,
				endAngle: GAUGE_START + GAUGE_TOTAL,
				hollow: { size: "70%" },
				dataLabels: { show: false },
			},
		},
		colors: ["transparent"],
		stroke: { width: 0 },
	});

	getBlobPosition = (value) => {
		const v = Math.max(0, Math.min(100, Number(value) || 0));
		const ang = ((GAUGE_START + GAUGE_TOTAL * (v / 100)) * Math.PI) / 180;
		return {
			left: GAUGE_CX + GAUGE_R * Math.cos(ang),
			top: GAUGE_CY + GAUGE_R * Math.sin(ang),
		};
	};

	componentDidMount() {
		const el = document.querySelector("#" + this.chartId);
		if (el) {
			const chart = new ApexCharts(el, this.buildChartOptions());
			chart.render();
			this.setState({ chart });
		}
	}

	componentWillUnmount() {
		if (this.state.chart) this.state.chart.destroy();
	}

	render() {
		const { value = 20 } = this.props;
		const blobPos = this.getBlobPosition(value);
		const severityIndex = getSeveritySegmentIndex(value);
		const blobBorderColor = SEGMENT_COLORS[severityIndex];
		const paths = [];
		for (let i = 0; i < 4; i++) {
			const a1 = GAUGE_START + i * GAUGE_SEG + GAUGE_GAP;
			const a2 = GAUGE_START + (i + 1) * GAUGE_SEG - GAUGE_GAP;
			paths.push({ d: arcD(GAUGE_CX, GAUGE_CY, GAUGE_R, a1, a2), color: SEGMENT_COLORS[i] });
		}
		const labelR = GAUGE_R + 14;
		const labels = [
			{ deg: GAUGE_START + GAUGE_SEG / 2, text: "Low" },
			{ deg: GAUGE_START + GAUGE_SEG * 1.5, text: "Medium" },
			{ deg: GAUGE_START + GAUGE_SEG * 2.5, text: "High" },
			{ deg: GAUGE_START + GAUGE_SEG * 3.5, text: "Critical" },
		];

		return (
			<div className="evs-attack-vector-gauge" ref={this.wrapperRef} style={{ width: GAUGE_SIZE, height: GAUGE_SIZE }}>
				<div id={this.chartId} className="evs-attack-vector-gauge__chart" />
				<svg
					className="evs-attack-vector-gauge__svg"
					width={GAUGE_SIZE}
					height={GAUGE_SIZE}
					viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}
				>
					{paths.map((p, i) => (
						<path
							key={i}
							d={p.d}
							stroke={p.color}
							strokeWidth={GAUGE_STROKE_WIDTH}
							fill="none"
							strokeLinecap="round"
						/>
					))}
					{labels.map((l, i) => {
						const p = polar(GAUGE_CX, GAUGE_CY, labelR, l.deg);
						const anchor = p.x < GAUGE_CX ? "end" : "start";
						return (
							<text
								key={i}
								x={p.x}
								y={p.y}
								textAnchor={anchor}
								dominantBaseline="middle"
								className="evs-attack-vector-gauge__segment-label"
							>
								{l.text}
							</text>
						);
					})}
				</svg>
				<div className="evs-attack-vector-gauge__center">
					<span className="evs-attack-vector-gauge__value">{value}%</span>
					<span className="evs-attack-vector-gauge__label">EVS Score</span>
				</div>
					<div
						ref={this.blobRef}
						className="evs-attack-vector-gauge__blob"
						aria-hidden
						style={{
							left: blobPos.left,
							top: blobPos.top,
							transform: "translate(-50%, -50%)",
							borderColor: blobBorderColor,
						}}
					/>
			</div>
		);
	}
}
